/**
 * AweniContext — Shared context singleton passed to all modules.
 * Provides auth, i18n, theme, notifications, navigation, and activity tracking.
 */

export interface UserInfo {
  id: number;
  email: string;
  name: string;
  role: string;
  avatar_url?: string;
  is_guest?: boolean;
  email_verified?: boolean;
  app_roles?: Record<string, string>;
}

export interface ProfileInfo {
  id: number;
  name: string;
  avatar_url?: string;
  type: 'owner' | 'child' | 'managed';
  role_badge?: string;
}

export interface NotificationItem {
  id: string;
  icon: string;
  title: string;
  body: string;
  time: string;
  read: boolean;
  action_url?: string;
  category: 'achievement' | 'reminder' | 'social' | 'system';
}

export interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  activeProfile: ProfileInfo | null;
  profiles: ProfileInfo[];
  switchProfile: (profileId: number) => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
  getToken: () => string | null;
}

export interface I18nState {
  locale: string;
  dir: 'ltr' | 'rtl';
  t: (key: string, params?: Record<string, string | number>) => string;
  setLocale: (locale: string) => void;
  supportedLocales: string[];
}

export interface ThemeState {
  isDark: boolean;
  toggle: () => void;
  setTheme: (dark: boolean) => void;
  onChange: (cb: (isDark: boolean) => void) => void;
}

export interface NotificationState {
  items: NotificationItem[];
  unreadCount: number;
  fetch: () => Promise<void>;
  markAllRead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
}

export interface OfflineState {
  isOnline: boolean;
  onStatusChange: (cb: (online: boolean) => void) => void;
}

export interface AweniContext {
  auth: AuthState;
  i18n: I18nState;
  theme: ThemeState;
  notifications: NotificationState;
  offline: OfflineState;
  navigate: (path: string) => void;
  trackActivity: (action: string, data?: Record<string, unknown>) => void;
  apiBase: string;
}

let _context: AweniContext | null = null;

export function getContext(): AweniContext {
  if (!_context) {
    throw new Error('AweniContext not initialized. Call setContext() first.');
  }
  return _context;
}

export function setContext(ctx: AweniContext): void {
  _context = ctx;
}

export function hasContext(): boolean {
  return _context !== null;
}
